import cx from "classnames";
import { MouseEvent, Ref, useCallback, useId } from "react";

import Box from "ds/components/Box";
import DropdownSectionItem from "ds/components/Dropdown/SectionItem";
import { BaseSelectItem } from "ds/components/Select/types";
import TextEllipsis from "ds/components/TextEllipsis";
import Toggle from "ds/components/Toggle";
import { TooltipProps } from "ds/components/Tooltip/types";
import Typography from "ds/components/Typography";
import { TypographyColors } from "types/Colors";

import styles from "./styles.module.css";

const getTickboxLabel = (isSwitch: boolean, isChecked: boolean) => {
  if (isSwitch) {
    return isChecked ? "Switch off" : "Switch on";
  }

  return isChecked ? "Unselect" : "Select";
};

type SelectOptionProps = {
  value: string;
  label: string;
  postfix?: string | number;
  postFixColor?: TypographyColors;
  onChange?: (value: string, option?: BaseSelectItem) => void;
  closeSelect?: () => void;
  // TODO: the `multiple` property is used for Dropdowns with multiple options, should be refactored and moved to DS Dropdown
  multiple?: boolean;
  checked?: boolean;
  className?: string;
  textClassName?: string;
  innerRef?: Ref<HTMLElement>;
  disabled?: boolean;
  tooltipPlacement?: TooltipProps["placement"];
  tooltipWidthMode?: TooltipProps["widthMode"];
  switchToggle?: boolean;
  highlightSelected?: boolean;
  textEllipsisMode?: "single" | "line-clamp";
  emptyDisplayLabel?: string;
};

const SelectOption = (props: SelectOptionProps) => {
  const {
    disabled,
    value,
    label,
    onChange,
    closeSelect,
    multiple,
    checked,
    postfix,
    postFixColor,
    className,
    textClassName,
    innerRef,
    tooltipPlacement = "top-end",
    tooltipWidthMode,
    switchToggle,
    highlightSelected = false,
    textEllipsisMode = "single",
    emptyDisplayLabel = "Empty",
  } = props;

  // If the label comes as an empty string (""), it is replaced with a visual placeholder.
  const displayLabel = label || emptyDisplayLabel;

  const id = useId();

  const handleOnChange = useCallback(
    (e?: MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      if (disabled) return false;

      onChange?.(value, {
        value,
        label,
      });

      if (!multiple) {
        closeSelect?.();
      }

      return undefined;
    },
    [closeSelect, disabled, label, multiple, onChange, value]
  );

  // TODO(accessibility): use label instead of button for list items
  return (
    <DropdownSectionItem
      active={highlightSelected && checked}
      onClick={handleOnChange}
      innerRef={innerRef}
      className={className}
      tabIndex={multiple ? -1 : 0}
    >
      <Box direction="row" align="center" gap="medium">
        {multiple && (
          <Toggle
            variant={switchToggle ? "switch" : "checkbox"}
            id={id}
            onChange={() => handleOnChange()}
            isSelected={checked}
            isDisabled={disabled}
            aria-label={getTickboxLabel(!!switchToggle, !!checked)}
          />
        )}

        <TextEllipsis
          tooltip={displayLabel}
          tooltipPlacement={tooltipPlacement}
          tooltipWidthMode={tooltipWidthMode}
          delay={400}
          mode={textEllipsisMode}
        >
          {(ellipsisProps) => (
            <Typography
              {...ellipsisProps}
              variant="p-body3"
              tag="span"
              className={cx(ellipsisProps.className, textClassName)}
            >
              {displayLabel}
            </Typography>
          )}
        </TextEllipsis>

        {typeof postfix !== "undefined" && (
          <Typography variant="p-body3" tag="span" className={styles.postfix} color={postFixColor}>
            {postfix}
          </Typography>
        )}
      </Box>
    </DropdownSectionItem>
  );
};

SelectOption.displayName = "DS.Select.Option";

export default SelectOption;
